import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { resolveOmniMediaUrl } from "../../api/client.ts";
import {
  attachLivePlayback,
  createVideoBufferingHandlers,
} from "../../lib/live-playback.ts";
import {
  PRESET_GRID_MARKER_SIZE_PX,
  PRESET_GRID_TOTAL,
  presetGridCenter360,
  presetGridIdFromYawPitch360,
} from "../../lib/presetGrid.ts";
import { PANORAMA_HORIZON_NORM_Y } from "../../lib/ptzPanoramaMap.ts";
import {
  equirectNormToPatrolHotspotDeg,
  patrolHotspotDegToEquirectNorm,
} from "../../pages/ConstructionSiteManagement/patrol/patrolHotspotProjection.js";
import { getPatrolHotspotMarkerIconUrl } from "../../pages/ConstructionSiteManagement/patrol/patrolHotspotIcons.js";
import {
  applyPatrolHotspotCircleMarkerStyle,
  createPatrolHotspotIconImg,
  PATROL_HOTSPOT_MARKER_SIZE_PX,
} from "../../pages/ConstructionSiteManagement/patrol/patrolHotspotMarkerDom.js";

const HORIZON_LINE_STEPS = 96;
const HORIZON_SEGMENT_BREAK_PX_FRAC = 0.35;

export type Pano360FocusRing = {
  normX: number;
  normY: number;
  /** 0..1 as a fraction of view width (same convention as flat panorama ring). */
  diameterFrac: number;
};

export type Pano360HotspotMarker = {
  id: string;
  yaw_deg: number;
  pitch_deg: number;
  label?: string;
  linkedCameraId?: string;
};

export type Pano360ImmersiveScene = {
  host: HTMLDivElement;
  canvas: HTMLCanvasElement;
  camera: THREE.PerspectiveCamera;
};

export type Pano360WebGLProps = {
  playlistUrl: string | null;
  streamType?: "hls" | "webrtc";
  enabled?: boolean;
  onStreamError: () => void;
  onBufferingChange?: (busy: boolean) => void;
  onStreamLoadingChange?: (loading: boolean) => void;
  /** Degrees per second. Default 0 (no autorotate). */
  autoRotateDps?: number;
  /** When true, a short click (no drag) maps to equirectangular 0..1 coords. */
  pickingEnabled?: boolean;
  /** Panorama texture coords: x left→right, y top→bottom (matches flat / server `applyFocus`). */
  onEquirectPick?: (normX: number, normY: number) => void;
  /** PTZ focus ring projected onto the sphere; omit or null to hide. */
  focusRing?: Pano360FocusRing | null;
  /** Camera hotspots pinned to the panorama (yaw/pitch from asset hotspots API). */
  hotspots?: Pano360HotspotMarker[];
  onHotspotClick?: (markerId: string) => void;
  /** When true, asset hotspots use PatrolAssetHotspotsLayer (all show types). */
  externalAssetHotspots?: boolean;
  /** Fired when the Three.js scene is ready (world-locked hotspot overlay). */
  onImmersiveSceneReady?: (scene: Pano360ImmersiveScene | null) => void;
  presetGridEnabled?: boolean;
  /** When set, only these grid ids render as preset markers (e.g. mapped presets only). */
  mappedPresetGridIds?: readonly number[];
  /** When true with preset grid, only numbered marker buttons select a cell (not panorama clicks). */
  presetGridMarkerPickOnly?: boolean;
  selectedPresetGridId?: number | null;
  onPresetGridSelect?: (gridId: number) => void;
  /** Short click without drag (e.g. PiP tap-to-swap main lens). */
  onShortTap?: () => void;
};

function gridIdFromNorm(normX: number, normY: number): number | null {
  const x = ((normX % 1) + 1) % 1;
  const y = Math.min(0.999999, Math.max(0, normY));
  const { yaw_deg } = equirectNormToPatrolHotspotDeg(x, y);
  return presetGridIdFromYawPitch360(yaw_deg, y);
}

export function Pano360WebGL({
  playlistUrl,
  streamType = "webrtc",
  enabled = true,
  onStreamError,
  onBufferingChange,
  onStreamLoadingChange,
  autoRotateDps = 0,
  pickingEnabled = false,
  onEquirectPick,
  focusRing = null,
  hotspots = [],
  onHotspotClick,
  externalAssetHotspots = false,
  onImmersiveSceneReady,
  presetGridEnabled = false,
  mappedPresetGridIds,
  presetGridMarkerPickOnly = false,
  selectedPresetGridId = null,
  onPresetGridSelect,
  onShortTap,
}: Pano360WebGLProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onErrRef = useRef(onStreamError);
  const onBufRef = useRef(onBufferingChange);
  const onLoadRef = useRef(onStreamLoadingChange);
  const pickEnabledRef = useRef(false);
  const onPickRef = useRef<typeof onEquirectPick>(undefined);
  const focusRingRef = useRef<Pano360FocusRing | null>(null);
  const hotspotsRef = useRef<Pano360HotspotMarker[]>([]);
  const onHotspotClickRef = useRef<typeof onHotspotClick>(undefined);
  const externalAssetHotspotsRef = useRef(false);
  const onImmersiveSceneReadyRef = useRef<typeof onImmersiveSceneReady>(undefined);
  const presetGridEnabledRef = useRef(false);
  const mappedPresetGridIdsRef = useRef<readonly number[] | undefined>(undefined);
  const presetGridMarkerPickOnlyRef = useRef(false);
  const selectedPresetGridIdRef = useRef<number | null>(null);
  const onPresetGridSelectRef = useRef<typeof onPresetGridSelect>(undefined);
  const onShortTapRef = useRef<typeof onShortTap>(undefined);
  onErrRef.current = onStreamError;
  onBufRef.current = onBufferingChange;
  onLoadRef.current = onStreamLoadingChange;
  pickEnabledRef.current = Boolean(pickingEnabled);
  onPickRef.current = onEquirectPick;
  focusRingRef.current = focusRing;
  hotspotsRef.current = hotspots;
  onHotspotClickRef.current = onHotspotClick;
  externalAssetHotspotsRef.current = Boolean(externalAssetHotspots);
  onImmersiveSceneReadyRef.current = onImmersiveSceneReady;
  presetGridEnabledRef.current = presetGridEnabled;
  mappedPresetGridIdsRef.current = mappedPresetGridIds;
  presetGridMarkerPickOnlyRef.current = presetGridMarkerPickOnly;
  selectedPresetGridIdRef.current = selectedPresetGridId;
  onPresetGridSelectRef.current = onPresetGridSelect;
  onShortTapRef.current = onShortTap;

  useEffect(() => {
    if (!enabled || !playlistUrl) return;
    const src = resolveOmniMediaUrl(playlistUrl);
    if (!src || !String(src).trim()) return;
    const host = hostRef.current;
    if (!host) return;

    let destroyed = false;
    let raf = 0;

    // Keep the <video> off-DOM; WebGL samples frames via VideoTexture.
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.controls = false;
    video.preload = "auto";
    video.crossOrigin = "anonymous";

    const buffering = createVideoBufferingHandlers((busy) =>
      onBufRef.current?.(busy),
    );
    buffering.attach(video);

    let loadSettled = false;
    const reportLoadEnd = () => {
      if (loadSettled) return;
      loadSettled = true;
      onLoadRef.current?.(false);
    };
    onLoadRef.current?.(true);
    video.addEventListener("playing", reportLoadEnd, { once: true });
    video.addEventListener("canplay", reportLoadEnd, { once: true });

    const playbackAbort = new AbortController();
    let playbackSession: ReturnType<typeof attachLivePlayback> = null;
    playbackSession = attachLivePlayback({
      playlistUrl,
      streamType,
      video,
      signal: playbackAbort.signal,
      onFatalError: () => {
        if (!destroyed) {
          reportLoadEnd();
          onErrRef.current?.();
          destroy();
        }
      },
      onRecovering: () => {
        if (!destroyed) onLoadRef.current?.(true);
      },
    });
    if (!playbackSession) {
      reportLoadEnd();
      onErrRef.current?.();
      return undefined;
    }

    const startThree = (): (() => void) | undefined => {
      if (destroyed) return undefined;

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        powerPreference: "high-performance",
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(host.clientWidth, host.clientHeight, false);
      renderer.setClearColor(0x0b1220, 1);
      host.appendChild(renderer.domElement);

      const ringEl = document.createElement("div");
      ringEl.className =
        "ds-preview-focus-ring ds-preview-focus-ring--immersive";
      ringEl.setAttribute("aria-hidden", "true");
      ringEl.style.display = "none";
      ringEl.style.position = "absolute";
      ringEl.style.zIndex = "4";
      ringEl.style.pointerEvents = "none";
      host.appendChild(ringEl);

      const horizonSvg = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "svg",
      );
      horizonSvg.setAttribute("class", "ds-preview-horizon-line-svg");
      horizonSvg.setAttribute("aria-hidden", "true");
      const horizonPath = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      horizonSvg.appendChild(horizonPath);
      host.appendChild(horizonSvg);

      const markerLayer = document.createElement("div");
      Object.assign(markerLayer.style, {
        position: "absolute",
        inset: "0",
        pointerEvents: "none",
        zIndex: "3",
      });
      host.appendChild(markerLayer);
      const markerEls = new Map<string, HTMLButtonElement>();

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1100);

      const geometry = new THREE.SphereGeometry(500, 60, 40);
      geometry.scale(-1, 1, 1); // flip normals so we render the inside
      const texture = new THREE.VideoTexture(video);
      texture.colorSpace = THREE.SRGBColorSpace ?? undefined;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      const material = new THREE.MeshBasicMaterial({ map: texture });
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      // View state: yaw/pitch in radians.
      let yaw = 0;
      let pitch = 0;
      const CLICK_PX = 10;
      let ptrDown = false;
      let dragRotate = false;
      let sx = 0;
      let sy = 0;
      let lastX = 0;
      let lastY = 0;
      let lastT = performance.now();

      const clamp = (v: number, lo: number, hi: number) =>
        Math.min(hi, Math.max(lo, v));
      const R = 500;

      /**
       * World-space point on the inner sphere for equirectangular (normX, normY)
       * matching Three.js `SphereGeometry` + `geometry.scale(-1,1,1)` — same inverse
       * as normsFromSphereHit().
       */
      const setWorldOnSphereFromNorm = (nx: number, ny: number) => {
        const uRow = clamp(nx, 0, 1);
        const vRow = clamp(ny, 0, 1);
        const phi = uRow * Math.PI * 2;
        const thetaT = vRow * Math.PI;
        const x0 = -R * Math.cos(phi) * Math.sin(thetaT);
        const y0 = R * Math.cos(thetaT);
        const z0 = R * Math.sin(phi) * Math.sin(thetaT);
        worldOnSphere.set(-x0, y0, z0);
      };

      /**
       * Inverts `setWorldOnSphereFromNorm` using the intersection world position
       * (stable) instead of triangle UVs (can be wrong across tessellation seams / poles).
       *
       * Azimuth must match Three.js `SphereGeometry`: φ = u·2π with u∈[0,1], i.e. φ∈[0,2π).
       */
      const normsFromSphereHit = (hit: {
        point: { x: number; y: number; z: number };
        uv?: { x: number; y: number };
      }) => {
        if (hit.uv && Number.isFinite(hit.uv.x) && Number.isFinite(hit.uv.y)) {
          return {
            normX: clamp(hit.uv.x, 0, 1),
            normY: clamp(1 - hit.uv.y, 0, 1),
          };
        }
        const p = hit.point;
        const len = Math.hypot(p.x, p.y, p.z);
        if (!Number.isFinite(len) || len < 1e-6) return null;
        const px = p.x / len;
        const py = p.y / len;
        const pz = p.z / len;
        const thetaT = Math.acos(Math.max(-1, Math.min(1, py)));
        const sinT = Math.sin(thetaT);
        let normX: number;
        if (Math.abs(sinT) < 1e-5) {
          normX = 0.5;
        } else {
          let phiU = Math.atan2(pz, px);
          if (phiU < 0) phiU += Math.PI * 2;
          normX = phiU / (Math.PI * 2);
        }
        const normY = clamp(thetaT / Math.PI, 0, 1);
        return { normX: clamp(normX, 0, 1), normY };
      };

      const updateCamera = () => {
        const phi = THREE.MathUtils.degToRad(90) - pitch;
        const theta = yaw;
        camera.target = new THREE.Vector3(
          R * Math.sin(phi) * Math.cos(theta),
          R * Math.cos(phi),
          R * Math.sin(phi) * Math.sin(theta),
        );
        camera.lookAt(camera.target);
      };

      const raycaster = new THREE.Raycaster();
      const ndc = new THREE.Vector2();
      const worldOnSphere = new THREE.Vector3();
      const projected = new THREE.Vector3();
      const camForward = new THREE.Vector3();
      const sphereDir = new THREE.Vector3();

      /** NDC for raycaster; must use the same canvas rect as projection → screen. */
      const clientToNdc = (clientX: number, clientY: number) => {
        const r = renderer.domElement.getBoundingClientRect();
        const w = Math.max(r.width, 1e-9);
        const h = Math.max(r.height, 1e-9);
        return {
          x: ((clientX - r.left) / w) * 2 - 1,
          y: -((clientY - r.top) / h) * 2 + 1,
        };
      };

      /** Map projected NDC → px `left`/`top` inside `host` (handles canvas offset vs host). */
      const projectWorldToHostCss = (world: {
        x: number;
        y: number;
        z: number;
      }) => {
        projected.copy(world).project(camera);
        const canvasR = renderer.domElement.getBoundingClientRect();
        const hostR = host.getBoundingClientRect();
        const xInCanvas = (projected.x * 0.5 + 0.5) * canvasR.width;
        const yInCanvas = (-projected.y * 0.5 + 0.5) * canvasR.height;
        return {
          left: canvasR.left - hostR.left + xInCanvas,
          top: canvasR.top - hostR.top + yInCanvas,
        };
      };

      const updateFocusRingOverlay = () => {
        const fr = focusRingRef.current;
        if (
          !fr ||
          !Number.isFinite(fr.normX) ||
          !Number.isFinite(fr.normY) ||
          !Number.isFinite(fr.diameterFrac) ||
          fr.diameterFrac <= 0
        ) {
          ringEl.style.display = "none";
          return;
        }
        setWorldOnSphereFromNorm(fr.normX, fr.normY);

        camera.getWorldDirection(camForward);
        sphereDir.copy(worldOnSphere).normalize();
        if (sphereDir.dot(camForward) < 0.04) {
          ringEl.style.display = "none";
          return;
        }

        const { left, top } = projectWorldToHostCss(worldOnSphere);
        const hostR = host.getBoundingClientRect();
        const hostW = Math.max(hostR.width, 1e-9);
        ringEl.style.display = "block";
        ringEl.style.left = `${left}px`;
        ringEl.style.top = `${top}px`;
        ringEl.style.width = `${fr.diameterFrac * hostW}px`;
        ringEl.style.aspectRatio = "1";
        ringEl.style.height = "auto";
        ringEl.style.transform = "translate(-50%, -50%)";
      };

      const updateHorizonLineOverlay = () => {
        const hostR = host.getBoundingClientRect();
        const hostW = Math.max(hostR.width, 1e-9);
        const hostH = Math.max(hostR.height, 1e-9);
        horizonSvg.setAttribute("width", String(hostW));
        horizonSvg.setAttribute("height", String(hostH));
        horizonSvg.style.width = `${hostW}px`;
        horizonSvg.style.height = `${hostH}px`;

        camera.getWorldDirection(camForward);
        const segments: { x: number; y: number }[][] = [];
        let current: { x: number; y: number }[] = [];
        const breakPx = hostW * HORIZON_SEGMENT_BREAK_PX_FRAC;

        for (let i = 0; i <= HORIZON_LINE_STEPS; i++) {
          const nx = i / HORIZON_LINE_STEPS;
          setWorldOnSphereFromNorm(nx, PANORAMA_HORIZON_NORM_Y);
          sphereDir.copy(worldOnSphere).normalize();
          if (sphereDir.dot(camForward) < 0.04) {
            if (current.length) {
              segments.push(current);
              current = [];
            }
            continue;
          }
          const { left, top } = projectWorldToHostCss(worldOnSphere);
          const pt = { x: left, y: top };
          if (current.length) {
            const prev = current[current.length - 1]!;
            if (Math.hypot(pt.x - prev.x, pt.y - prev.y) > breakPx) {
              segments.push(current);
              current = [pt];
              continue;
            }
          }
          current.push(pt);
        }
        if (current.length) segments.push(current);

        const d = segments
          .map((pts) =>
            pts
              .map(
                (p, idx) =>
                  `${idx === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`,
              )
              .join(" "),
          )
          .join(" ");

        if (!d) {
          horizonSvg.style.display = "none";
          return;
        }
        horizonSvg.style.display = "block";
        horizonPath.setAttribute("d", d);
      };

      const syncHotspotMarkers = () => {
        const hotspotList = externalAssetHotspotsRef.current
          ? []
          : hotspotsRef.current.map((m) => ({
              ...m,
              kind: "hotspot" as const,
            }));
        const presetGridIds = presetGridEnabledRef.current
          ? mappedPresetGridIdsRef.current != null
            ? [...mappedPresetGridIdsRef.current]
            : Array.from({ length: PRESET_GRID_TOTAL }, (_, idx) => idx + 1)
          : [];
        const presetList = presetGridIds.map((gridId) => {
              const center = presetGridCenter360(gridId);
              return {
                id: `__preset_grid_${gridId}`,
                yaw_deg: center.yawDeg,
                pitch_deg: center.pitchDeg,
                label: `Grid ${gridId}`,
                kind: "preset" as const,
                gridId,
              };
            });

        const list = [...hotspotList, ...presetList];
        const ids = new Set(list.map((m) => m.id));
        for (const id of [...markerEls.keys()]) {
          if (!ids.has(id)) {
            markerEls.get(id)?.remove();
            markerEls.delete(id);
          }
        }

        for (const m of list) {
          const eq = patrolHotspotDegToEquirectNorm(m.yaw_deg, m.pitch_deg);
          setWorldOnSphereFromNorm(eq.normX, eq.normY);

          camera.getWorldDirection(camForward);
          sphereDir.copy(worldOnSphere).normalize();
          const visible = sphereDir.dot(camForward) > 0.04;

          let el = markerEls.get(m.id);
          if (!el) {
            el = document.createElement("button");
            el.type = "button";
            el.addEventListener("pointerdown", (ev) => ev.stopPropagation());
            el.addEventListener("pointerup", (ev) => {
              ev.stopPropagation();
              if (m.kind === "preset") {
                onPresetGridSelectRef.current?.(m.gridId);
                return;
              }
              onHotspotClickRef.current?.(m.id);
            });
            markerLayer.appendChild(el);
            markerEls.set(m.id, el);
          }

          if (m.kind === "preset") {
            const selected = selectedPresetGridIdRef.current === m.gridId;
            Object.assign(el.style, {
              position: "absolute",
              borderRadius: "50%",
              border: selected
                ? "2px solid #FACC15"
                : "2px solid rgba(255,255,255,0.9)",
              background: selected
                ? "rgba(37,99,235,0.96)"
                : "rgba(15,23,42,0.8)",
              color: "#fff",
              fontWeight: "700",
              fontSize: "13px",
              lineHeight: "1",
              padding: "0",
              boxShadow: "0 0 0 2px rgba(0,0,0,0.35)",
              pointerEvents: "auto",
              cursor: "pointer",
              zIndex: "4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            });
            el.textContent = String(m.gridId);
            el.title = `Grid ${m.gridId}`;
            el.setAttribute("aria-label", `Grid ${m.gridId}`);
          } else {
            applyPatrolHotspotCircleMarkerStyle(el, { zIndex: "4" });
            if (!el.dataset.patrolIconMounted) {
              el.replaceChildren(
                createPatrolHotspotIconImg(
                  getPatrolHotspotMarkerIconUrl("camera"),
                ),
              );
              el.dataset.patrolIconMounted = "1";
            }
            el.title = m.label ?? "Camera";
            el.setAttribute("aria-label", m.label ?? "Linked camera");
          }

          if (!visible) {
            el.style.display = "none";
            continue;
          }

          const { left, top } = projectWorldToHostCss(worldOnSphere);
          const size =
            m.kind === "preset"
              ? PRESET_GRID_MARKER_SIZE_PX
              : PATROL_HOTSPOT_MARKER_SIZE_PX;
          el.style.display = "flex";
          el.style.left = `${left}px`;
          el.style.top = `${top}px`;
          el.style.width = `${size}px`;
          el.style.height = `${size}px`;
          el.style.transform = "translate(-50%, -50%)";
        }
      };

      const onPointerDown = (e: PointerEvent) => {
        if (e.button !== 0) return;
        ptrDown = true;
        dragRotate = false;
        sx = lastX = e.clientX;
        sy = lastY = e.clientY;
        renderer.domElement.style.cursor = "grabbing";
        try {
          renderer.domElement.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      };
      const onPointerMove = (e: PointerEvent) => {
        if (!ptrDown) return;
        const dx0 = e.clientX - sx;
        const dy0 = e.clientY - sy;
        if (!dragRotate && dx0 * dx0 + dy0 * dy0 > CLICK_PX * CLICK_PX) {
          dragRotate = true;
          lastX = e.clientX;
          lastY = e.clientY;
          return;
        }
        if (!dragRotate) return;
        const mdx = e.clientX - lastX;
        const mdy = e.clientY - lastY;
        lastX = e.clientX;
        lastY = e.clientY;
        yaw -= mdx * 0.0035;
        // Match common panorama UX: drag/scroll down tilts view up (grab-the-world).
        pitch += mdy * 0.0035;
        pitch = clamp(pitch, -1.2, 1.2);
      };
      /**
       * Multi-ray pick (~3×3 px in NDC) so stretched poles / screen edges still
       * resolve to a stable aim. `normX` uses circular mean (wrap-safe at u=0/1).
       */
      const samplePickNormFromNdc = (ndcCx: number, ndcCy: number) => {
        const r = renderer.domElement.getBoundingClientRect();
        const stepX = (3 * 2) / Math.max(r.width, 1);
        const stepY = (3 * 2) / Math.max(r.height, 1);
        let sumSin = 0;
        let sumCos = 0;
        let sumY = 0;
        let n = 0;
        for (let j = -1; j <= 1; j++) {
          for (let i = -1; i <= 1; i++) {
            ndc.x = clamp(ndcCx + i * stepX, -1, 1);
            ndc.y = clamp(ndcCy + j * stepY, -1, 1);
            raycaster.setFromCamera(ndc, camera);
            const hits = raycaster.intersectObject(mesh, false);
            const hit = hits[0];
            if (!hit?.point) continue;
            const norms = normsFromSphereHit(hit);
            if (!norms) continue;
            const ang = norms.normX * Math.PI * 2;
            sumSin += Math.sin(ang);
            sumCos += Math.cos(ang);
            sumY += norms.normY;
            n++;
          }
        }
        if (n === 0) return null;
        let phiMean = Math.atan2(sumSin / n, sumCos / n);
        if (phiMean < 0) phiMean += Math.PI * 2;
        const normX = clamp(phiMean / (Math.PI * 2), 0, 1);
        const normY = clamp(sumY / n, 0, 1);
        return { normX, normY };
      };

      const onPointerUp = (e: PointerEvent) => {
        if (!ptrDown) return;
        ptrDown = false;
        try {
          renderer.domElement.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        if (!dragRotate && e.button === 0) {
          const panoramaPresetPick =
            presetGridEnabledRef.current &&
            !presetGridMarkerPickOnlyRef.current;
          if (pickEnabledRef.current || panoramaPresetPick) {
            updateCamera();
            camera.updateMatrixWorld(true);
            mesh.updateMatrixWorld(true);
            const p = clientToNdc(e.clientX, e.clientY);
            const norms = samplePickNormFromNdc(p.x, p.y);
            if (norms) {
              if (panoramaPresetPick) {
                const gridId = gridIdFromNorm(norms.normX, norms.normY);
                if (gridId !== null) onPresetGridSelectRef.current?.(gridId);
              } else if (onPickRef.current) {
                onPickRef.current(norms.normX, norms.normY);
              }
            }
          } else {
            onShortTapRef.current?.();
          }
        }
        dragRotate = false;
        renderer.domElement.style.cursor = "crosshair";
      };
      const onPointerCancel = (e: PointerEvent) => {
        if (!ptrDown) return;
        ptrDown = false;
        dragRotate = false;
        renderer.domElement.style.cursor = "grab";
        try {
          renderer.domElement.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      };

      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";
      renderer.domElement.style.display = "block";
      renderer.domElement.style.cursor = "grab";
      const onWheel = (e: WheelEvent) => {
        e.preventDefault();
        const k = 0.0012;
        pitch += e.deltaY * k;
        pitch = clamp(pitch, -1.2, 1.2);
      };

      renderer.domElement.addEventListener("pointerdown", onPointerDown);
      renderer.domElement.addEventListener("pointermove", onPointerMove);
      renderer.domElement.addEventListener("pointerup", onPointerUp);
      renderer.domElement.addEventListener("pointercancel", onPointerCancel);
      renderer.domElement.addEventListener("wheel", onWheel, {
        passive: false,
      });

      const resize = () => {
        const w = host.clientWidth || 1;
        const h = host.clientHeight || 1;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h, false);
      };
      const ro = new ResizeObserver(resize);
      ro.observe(host);
      resize();

      const animate = () => {
        if (destroyed) return;
        const now = performance.now();
        const dt = Math.min(0.1, (now - lastT) / 1000);
        lastT = now;
        if (!ptrDown && autoRotateDps > 0) {
          yaw += THREE.MathUtils.degToRad(autoRotateDps) * dt;
        }
        updateCamera();
        camera.updateMatrixWorld(true);
        renderer.render(scene, camera);
        updateFocusRingOverlay();
        updateHorizonLineOverlay();
        syncHotspotMarkers();
        raf = requestAnimationFrame(animate);
      };
      onImmersiveSceneReadyRef.current?.({
        host,
        canvas: renderer.domElement,
        camera,
      });

      animate();

      return () => {
        onImmersiveSceneReadyRef.current?.(null);
        ro.disconnect();
        renderer.domElement.removeEventListener("pointerdown", onPointerDown);
        renderer.domElement.removeEventListener("pointermove", onPointerMove);
        renderer.domElement.removeEventListener("pointerup", onPointerUp);
        renderer.domElement.removeEventListener(
          "pointercancel",
          onPointerCancel,
        );
        renderer.domElement.removeEventListener(
          "wheel",
          onWheel as EventListener,
        );
        try {
          host.removeChild(renderer.domElement);
        } catch {
          /* ignore */
        }
        try {
          host.removeChild(ringEl);
        } catch {
          /* ignore */
        }
        try {
          host.removeChild(horizonSvg);
        } catch {
          /* ignore */
        }
        try {
          host.removeChild(markerLayer);
        } catch {
          /* ignore */
        }
        markerEls.clear();
        texture.dispose?.();
        material.dispose?.();
        geometry.dispose?.();
        renderer.dispose?.();
      };
    };

    let stopThree: (() => void) | undefined;
    try {
      stopThree = startThree();
    } catch {
      reportLoadEnd();
      onErrRef.current?.();
    }

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      playbackAbort.abort();
      playbackSession?.destroy();
      playbackSession = null;
      buffering.detach(video);
      buffering.clear();
      reportLoadEnd();
      video.removeEventListener("playing", reportLoadEnd);
      video.removeEventListener("canplay", reportLoadEnd);
      stopThree?.();
    };

    return () => destroy();
  }, [autoRotateDps, enabled, playlistUrl, streamType]);

  return (
    <div
      ref={hostRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: "#0b1220",
        overflow: "hidden",
        touchAction: "none",
        cursor: "grab",
      }}
    />
  );
}
